import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import PublicNav from '../components/PublicNav';
import PublicFooter from '../components/PublicFooter';
import {
  Brain, FileText, Headphones, ClipboardCheck, Users, BarChart3,
  Shield, Zap, ArrowRight, CheckCircle2, Upload, Bell, CreditCard
} from 'lucide-react';

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } },
};

const capabilities = [
  {
    icon: Brain,
    title: 'AI-Powered Assessment',
    desc: 'GPT-5.2 analyzes every conversation against your SOPs, identifying violations, missed steps, and opportunities for improvement.',
    features: [
      'Automatic transcription via Whisper',
      'Section-by-section compliance scoring',
      'Weighted parameter evaluation',
      'Actionable recommendations',
    ],
    image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&q=60',
  },
  {
    icon: FileText,
    title: 'SOP & Checklist Management',
    desc: 'Create and manage Standard Operating Procedures with custom parameters, severity levels, and weighted scoring systems.',
    features: [
      'Upload PDF or text-based SOPs',
      'Custom parameter weightage',
      'Severity-based scoring (Critical/Major/Minor)',
      'Pre-built audit templates',
    ],
    image: null,
  },
  {
    icon: Headphones,
    title: 'Live Call Monitoring',
    desc: 'Connect your cloud telephony provider for real-time call tracking, status updates, and instant quality insights.',
    features: [
      'Knowlarity & Ozonetel integration',
      'Real-time call status tracking',
      'Auto-download recordings on call end',
      'Live statistics dashboard',
    ],
    image: 'https://images.unsplash.com/photo-1658953229625-aad99d7603b4?w=800&q=60',
  },
  {
    icon: ClipboardCheck,
    title: 'Custom Audit Sheets',
    desc: 'Build structured audit forms with scored questions, or use our standard templates for Customer Service, Sales, and Compliance.',
    features: [
      '4 standard templates included',
      'Custom section & question builder',
      'Per-question severity and scoring',
      'Manual audit submission',
    ],
    image: null,
  },
  {
    icon: Users,
    title: 'Team & Agent Management',
    desc: 'Multi-role system with Admin and Reviewer roles, team organization, and per-agent performance tracking.',
    features: [
      'Admin & Reviewer roles',
      'Team-based organization',
      'Agent performance metrics',
      'Bulk upload via CSV',
    ],
    image: 'https://images.unsplash.com/photo-1758518731468-98e90ffd7430?w=800&q=60',
  },
  {
    icon: Bell,
    title: 'Smart Notifications',
    desc: 'Get instant alerts when call scores fall below your threshold. Configurable per-user with email and in-app delivery.',
    features: [
      'Configurable score threshold',
      'In-app notification bell',
      'Email alerts via Resend',
      'Per-user notification settings',
    ],
    image: null,
  },
];

const CapabilitiesPage = () => {
  return (
    <div className="bg-[#050505] min-h-screen text-white" data-testid="capabilities-page">
      <PublicNav />

      {/* Hero */}
      <section className="pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div initial="hidden" animate="visible" className="max-w-3xl">
            <motion.p variants={fadeUp} className="text-xs font-bold tracking-[0.2em] uppercase text-zinc-500 mb-4">
              Capabilities
            </motion.p>
            <motion.h1
              variants={fadeUp}
              className="text-5xl md:text-6xl font-medium tracking-tighter leading-[1.1] mb-6"
              style={{ fontFamily: "'Clash Display', sans-serif" }}
            >
              Everything you need to{' '}
              <span className="bg-gradient-to-r from-[#FF3366] via-[#FF8C00] to-[#FFD700] bg-clip-text text-transparent">
                master call quality
              </span>
            </motion.h1>
            <motion.p variants={fadeUp} className="text-lg text-zinc-400 leading-relaxed" style={{ fontFamily: "'Outfit', sans-serif" }}>
              From AI transcription to live monitoring, CallScript AI gives your team the complete toolkit for call compliance and performance improvement.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Capability Sections */}
      {capabilities.map((cap, i) => {
        const Icon = cap.icon;
        const isReversed = i % 2 !== 0;

        return (
          <section
            key={cap.title}
            className={`py-20 ${i % 2 === 0 ? 'bg-[#050505]' : 'bg-[#0F0F11]'}`}
            data-testid={`capability-section-${i}`}
          >
            <div className="max-w-7xl mx-auto px-6 lg:px-12">
              <div className={`grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center ${isReversed ? 'lg:flex-row-reverse' : ''}`}>
                {/* Text */}
                <motion.div
                  initial={{ opacity: 0, x: isReversed ? 30 : -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6 }}
                  className={isReversed ? 'lg:order-2' : ''}
                >
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#FF3366]/20 to-[#FF8C00]/20 flex items-center justify-center mb-6">
                    <Icon size={28} className="text-[#FF3366]" />
                  </div>
                  <h2
                    className="text-3xl md:text-4xl font-medium tracking-tight mb-4"
                    style={{ fontFamily: "'Clash Display', sans-serif" }}
                  >
                    {cap.title}
                  </h2>
                  <p className="text-zinc-400 leading-relaxed mb-6" style={{ fontFamily: "'Outfit', sans-serif" }}>
                    {cap.desc}
                  </p>
                  <ul className="space-y-3">
                    {cap.features.map((f) => (
                      <li key={f} className="flex items-start gap-3">
                        <CheckCircle2 size={18} className="text-[#FF8C00] mt-0.5 flex-shrink-0" />
                        <span className="text-sm text-zinc-300">{f}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>

                {/* Image / Visual */}
                <motion.div
                  initial={{ opacity: 0, x: isReversed ? -30 : 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.1 }}
                  className={isReversed ? 'lg:order-1' : ''}
                >
                  {cap.image ? (
                    <div className="rounded-3xl overflow-hidden border border-white/5">
                      <img
                        src={cap.image}
                        alt={cap.title}
                        className="w-full h-64 lg:h-80 object-cover opacity-80"
                        loading="lazy"
                      />
                    </div>
                  ) : (
                    <div className="rounded-3xl bg-[#1A1A1D] border border-white/5 p-10 flex items-center justify-center h-64 lg:h-80">
                      <Icon size={80} className="text-white/5" />
                    </div>
                  )}
                </motion.div>
              </div>
            </div>
          </section>
        );
      })}

      {/* CTA */}
      <section className="py-24 lg:py-32 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 text-center">
          <h2
            className="text-4xl md:text-5xl font-medium tracking-tight mb-4"
            style={{ fontFamily: "'Clash Display', sans-serif" }}
          >
            See it in action
          </h2>
          <p className="text-zinc-400 text-lg max-w-xl mx-auto mb-8">
            Get started with a free account or book a personalized demo with our team.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/login?tab=register"
              className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#FF3366] to-[#FF8C00] text-white px-8 py-3.5 font-semibold hover:scale-105 transition-transform shadow-[0_0_20px_rgba(255,51,102,0.3)]"
              data-testid="capabilities-cta-signup"
            >
              Start Free <ArrowRight size={18} />
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 rounded-full bg-white/5 border border-white/10 text-white px-8 py-3.5 font-semibold hover:bg-white/10 transition-all"
              data-testid="capabilities-cta-demo"
            >
              Request Demo
            </Link>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
};

export default CapabilitiesPage;
